import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (refreshUser) refreshUser();
          if (data.booking_id) {
            navigate(`/booking/${data.booking_id}/confirmation`, { replace: true });
          }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch { /* keep polling */ }
      if (attempts.current >= 8) { setStatus("failed"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, navigate, refreshUser]);

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 gradient-hero">
      <div className="glass neon-border rounded-3xl p-10 max-w-md w-full text-center fade-up" data-testid="payment-result">
        {status === "checking" && (
          <>
            <Loader2 className="w-14 h-14 text-cyan-400 animate-spin mx-auto mb-6" />
            <h1 className="font-heading text-2xl font-bold text-white">Confirming your booking…</h1>
            <p className="text-slate-400 mt-2 text-sm">Securely verifying your payment with Stripe.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full btn-neon flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-9 h-9" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-white">Booking confirmed! 🎉</h1>
            <p className="text-slate-400 mt-2 text-sm">Your Caribbean escape is locked in. Check your dashboard for trip details.</p>
            <Link to="/dashboard" data-testid="view-trips-btn" className="inline-flex items-center gap-2 mt-7 btn-neon rounded-full px-7 py-3 font-semibold">
              View my trips <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        )}
        {status === "failed" && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#FF5E7E]/15 border border-[#FF5E7E]/40 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-9 h-9 text-[#FF5E7E]" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-white">Couldn't confirm payment</h1>
            <p className="text-slate-400 mt-2 text-sm">If you were charged, your booking will appear in your dashboard shortly.</p>
            <Link to="/browse" className="inline-flex items-center gap-2 mt-7 glass rounded-full px-7 py-3 font-semibold text-slate-200">
              Back to browse
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
